/**
 * Утилиты для обнаружения и определения типов камер
 * Изолированная логика работы с MediaDevices API
 */

import type { CameraInfo } from '../types';
import { CAMERA_TYPE_PATTERNS } from '../constants';

/**
 * Определяет тип камеры по её label
 */
export function detectCameraType(label: string): 'front' | 'back' | 'unknown' {
  const lowerLabel = label.toLowerCase();

  if (CAMERA_TYPE_PATTERNS.back.test(lowerLabel)) {
    return 'back';
  }
  if (CAMERA_TYPE_PATTERNS.front.test(lowerLabel)) {
    return 'front';
  }

  return 'unknown';
}

/**
 * Определяет доступные камеры на устройстве
 */
export async function enumerateCameras(): Promise<CameraInfo[]> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');

    if (videoDevices.length === 0) {
      console.warn('⚠️ Камеры не найдены');
      return [];
    }

    return videoDevices.map((device, index) => {
      const type = detectCameraType(device.label);
      
      return {
        deviceId: device.deviceId,
        label: device.label || `Камера ${index + 1}`,
        type,
        facingMode: type === 'front' ? 'user' : type === 'back' ? 'environment' : undefined
      };
    });
  } catch (error) {
    console.error('❌ Ошибка определения камер:', error);
    return [];
  }
}

/**
 * Определяет доступные аудио входные устройства
 */
export async function enumerateAudioInputDevices() {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter(device => device.kind === 'audioinput')
      .map(device => ({
        deviceId: device.deviceId,
        label: device.label || `Микрофон ${device.deviceId.slice(0, 8)}`,
        kind: 'audioinput' as const
      }));
  } catch (error) {
    console.error('❌ Ошибка определения аудио входов:', error);
    return [];
  }
}

/**
 * Определяет доступные аудио выходные устройства
 */
export async function enumerateAudioOutputDevices() {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter(device => device.kind === 'audiooutput')
      .map(device => ({
        deviceId: device.deviceId,
        label: device.label || `Динамики ${device.deviceId.slice(0, 8)}`,
        kind: 'audiooutput' as const
      }));
  } catch (error) {
    console.error('❌ Ошибка определения аудио выходов:', error);
    return [];
  }
}

/**
 * Определяет является ли устройство мобильным
 */
export function isMobileDevice(): boolean {
  return MOBILE_REGEX.test(navigator.userAgent);
}

/**
 * Regex для определения мобильного устройства
 */
const MOBILE_REGEX = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

